import { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { Stack } from 'react-bootstrap';
import ConfirmationModal from '../../../components/modals/ConfirmationModal';
import { LinkCell } from '../../../components/tables/cellComponents/LinkCell';
import { useToast } from '../../../context/ToastContext';
import { userCanEditCreateDelete_Waviers } from '../../../features/dash/views/people/Waiver/WaiversView';
import { Waiver } from '../../../services/endpoints/people/user';
import { useDeleteWaiverMutation } from '../../../services/endpoints/people/waiver';
import { getCurrentUser } from '../../../services/helper';
import { Course } from '../../../types/course.type';
import { getErrorMessage } from '../../utils';

const DeleteWaiverButton: React.FC<{ id: number }> = ({ id }) => {
  const [showModal, setShowModal] = useState(false);
  const [deleteWaiver, { isLoading }] = useDeleteWaiverMutation();
  const { addToast } = useToast();

  const handleDelete = async () => {
    try {
      await deleteWaiver(id).unwrap();
      addToast('Waiver deleted successfully', 'success');
      setShowModal(false);
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  return (
    <>
      <button
        className="text-info fw-bolder btn btn-link p-0"
        style={{ fontSize: '.875rem', textDecoration: 'underline' }}
        onClick={() => setShowModal(true)}
      >
        Delete
      </button>
      <ConfirmationModal
        show={showModal}
        title="Delete Waiver"
        message="Are you sure you want to delete this waiver?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowModal(false)}
        isLoading={isLoading}
      />
    </>
  );
};

const currentUser = getCurrentUser();
export const waiverColumns: ColumnDef<Waiver>[] = [
  {
    header: 'ID',
    accessorKey: 'id',
    cell: ({ getValue }) => (
      <LinkCell
        content={getValue<number>()}
        url={`/admin/waivers/${getValue<number>()}`}
      />
    )
  },
  {
    header: 'Session',
    accessorKey: 'course',
    cell: ({ getValue }) => (
      <LinkCell
        content={getValue<Course>().title}
        url={`/admin/schedule/sessions/${getValue<Course>().id}`}
      />
    )
  },
  {
    header: 'Child First Name',
    accessorKey: 'child_first_name'
  },
  {
    header: 'Child Last Name',
    accessorKey: 'child_last_name'
  },
  {
    header: 'Parent First Name',
    accessorKey: 'parent_first_name'
  },
  {
    header: 'Parent Last Name',
    accessorKey: 'parent_last_name'
  },
  {
    header: 'Parent Email Address',
    accessorKey: 'parent_email_address'
  },
  {
    header: 'Parent Mobile Phone',
    accessorKey: 'parent_mobile_number'
  },
  {
    header: () => null,
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => (
      <Stack
        direction="horizontal"
        gap={2}
      >
        <LinkCell
          content="View"
          url={`/admin/waivers/${row.original.id}`}
          underline
        />
        {userCanEditCreateDelete_Waviers(currentUser) && (
          <DeleteWaiverButton id={row.original.id!} />
        )}
      </Stack>
    )
  }
];
